import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CartItem, Product, ProductVariant } from '@core/models';
import { StoreConfigService } from './store-config.service';
import { addQty, decimalsOf, roundQty, stepOf } from '../../shared/utils/quantity-rules.util';

const STORAGE_KEY = 'ecommerce_cart_v2';
/** v1 era un carrito único para todas las tiendas, sin saber de cuál era cada producto. */
const LEGACY_STORAGE_KEY = 'ecommerce_cart_v1';

/** Carrito de una tienda; nombre y logo para avisarlo fuera de ella (portada). */
export interface StoreCart {
  storeName: string;
  logoUrl?: string;
  items: CartItem[];
}

/**
 * Un carrito por tienda (slug), persistido en localStorage: cada compra es de una sola
 * tienda, así que la tienda B no ve ni manda al checkout lo agregado en la tienda A.
 * Fuera de una tienda (portada, cuenta) el carrito actual está vacío; `storeCarts` los lista.
 */
@Injectable({ providedIn: 'root' })
export class CartService {
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private storeConfig = inject(StoreConfigService);

  private carts = signal<Record<string, StoreCart>>(this.load());
  private items = computed(() => this.carts()[this.storeConfig.currentSlug()]?.items ?? []);

  /** Tiendas con productos en el carrito, para la portada. */
  readonly storeCarts = computed(() =>
    Object.entries(this.carts())
      .map(([slug, cart]) => ({ slug, ...cart, totalItems: cart.items.reduce((sum, i) => sum + i.quantity, 0) }))
      .filter((c) => c.totalItems > 0),
  );

  readonly cartItems = this.items;
  readonly totalItems = computed(() => this.items().reduce((sum, i) => sum + i.quantity, 0));

  readonly subtotal = computed(() =>
    this.items().reduce((sum, i) => sum + (i.product.price + (i.variant?.priceDelta ?? 0)) * i.quantity, 0),
  );

  /**
   * Total informativo: el precio real lo resuelve store-gtw contra el catálogo al crear
   * el pedido. Se quitaron los cupones (lista fija en el front, el backend no los aplica)
   * y el envío fijo de $9.99 que nadie cobraba: el envío se coordina por WhatsApp.
   */
  readonly total = this.subtotal;

  /** Unidades de un producto en el carrito, sumando todas sus variantes. */
  quantityOf(productId: string): number {
    return this.items().reduce((sum, i) => (i.product.id === productId ? sum + i.quantity : sum), 0);
  }

  private lineKey(productId: string, variantId?: string): string {
    return variantId ? `${productId}::${variantId}` : productId;
  }

  /**
   * Tope por línea según las existencias conocidas. Es solo una ayuda: el stock guardado
   * en el carrito puede haber envejecido, así que store-gtw revalida al crear el pedido.
   */
  private capToStock(product: Product, variant: ProductVariant | undefined, quantity: number): number {
    const available = variant?.stock ?? product.stock;
    if (available === null || available === undefined) return quantity;
    return Math.max(stepOf(product.unit), Math.min(roundQty(quantity, decimalsOf(product.unit)), available));
  }

  /** Devuelve cuántas unidades se agregaron de verdad: puede ser menos (o 0) por el stock. */
  add(product: Product, quantity = 1, variant?: ProductVariant): number {
    const before = this.quantityOf(product.id);
    this.updateItems((list) => {
      const key = this.lineKey(product.id, variant?.id);
      const existing = list.find((i) => this.lineKey(i.product.id, i.variant?.id) === key);
      if (existing) {
        return list.map((i) =>
          this.lineKey(i.product.id, i.variant?.id) === key
            ? { ...i, quantity: this.capToStock(product, variant, addQty(i.quantity, quantity)) }
            : i,
        );
      }
      return [...list, { product, variant, quantity: this.capToStock(product, variant, quantity) }];
    });
    return this.quantityOf(product.id) - before;
  }

  updateQuantity(productId: string, quantity: number, variantId?: string): void {
    if (quantity <= 0) {
      this.remove(productId, variantId);
      return;
    }
    const key = this.lineKey(productId, variantId);
    this.updateItems((list) =>
      list.map((i) => (this.lineKey(i.product.id, i.variant?.id) === key ? { ...i, quantity: roundQty(quantity, decimalsOf(i.product.unit)) } : i)),
    );
  }

  remove(productId: string, variantId?: string): void {
    const key = this.lineKey(productId, variantId);
    this.updateItems((list) => list.filter((i) => this.lineKey(i.product.id, i.variant?.id) !== key));
  }

  clear(): void {
    this.updateItems(() => []);
  }

  /** Cambia el carrito de la tienda actual; sin tienda no hay carrito que tocar. */
  private updateItems(fn: (list: CartItem[]) => CartItem[]): void {
    const slug = this.storeConfig.currentSlug();
    if (!slug) return;
    this.carts.update((carts) => {
      const items = fn(carts[slug]?.items ?? []);
      const { [slug]: previous, ...others } = carts;
      if (!items.length) return others;
      // Mientras carga la tienda, branding es el de ejemplo: se queda el nombre ya guardado.
      const branding = this.storeConfig.status() === 'ready' ? this.storeConfig.branding() : null;
      const storeName = branding?.siteName || previous?.storeName || slug;
      const logoUrl = branding ? branding.logoUrl : previous?.logoUrl;
      return { ...others, [slug]: { storeName, logoUrl, items } };
    });
    this.persist();
  }

  private load(): Record<string, StoreCart> {
    if (!this.isBrowser) return {};
    try {
      // ponytail: el carrito v1 se descarta; no se sabe de qué tienda era cada producto.
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  private persist(): void {
    if (!this.isBrowser) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.carts()));
    } catch {
      /* localStorage no disponible (modo privado, etc.) */
    }
  }
}
